import * as React from "react";
import {
    Popover,
    IconButton,
    Divider,
    RadioGroup,
    FormControlLabel,
    Radio,
    Typography,
    Button,
} from "@mui/material";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";

export type FilterSection = {
    heading: string;
    options: { label: string; value: string }[];
    /** ค่าที่เลือก (controlled จากภายนอก) */
    value: string;
    /** อัพเดทค่าใน section นี้ */
    onChange: (value: string) => void;
};

type PopoverFilterProps = {
    sections: FilterSection[];
    className?: string;                  // เพิ่มคลาส tailwind ภายนอกได้
    onApply?: () => void;                // กดยืนยัน
    onClear?: () => void;                // ล้างค่าทั้งหมด
    hidden_actions?: boolean;
    /** ใช้ trigger เองได้ ถ้าไม่ได้ส่ง จะใช้ IconButton ค่าเริ่มต้น */
    renderTrigger?: (props: { onOpen: (e: React.MouseEvent<HTMLElement>) => void; open: boolean }) => React.ReactNode;
    popoverWidth?: number | string;      // ปรับความกว้าง
};

const PopoverFilter: React.FC<PopoverFilterProps> = ({
    sections,
    className,
    onApply,
    onClear,
    renderTrigger,
    popoverWidth = 320,
    hidden_actions
}) => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    return (
        <>
            {renderTrigger ? (
                renderTrigger({ onOpen: handleOpen, open })
            ) : (
                <IconButton
                    onClick={handleOpen}
                    aria-label="filter"
                    className={`rounded-2xl shadow-sm border border-gray-200 ${className ?? ""}`}
                >
                    <FilterListRoundedIcon />
                </IconButton>
            )}

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                    className:
                        "rounded-3xl border border-gray-100 shadow-xl",
                    style: { width: typeof popoverWidth === "number" ? `${popoverWidth}px` : popoverWidth },
                }}
            >
                <div className="bg-white p-5">
                    {sections.map((section, idx) => (
                        <div key={section.heading} className={idx > 0 ? "pt-2" : ""}>
                            {idx > 0 && <Divider sx={{ my: 1 }} />}
                            {section?.options?.length > 0 && (
                                <Typography variant="subtitle1" className="font-bold text-gray-800 mb-2">
                                    {section.heading}
                                </Typography>
                            )}
                            <RadioGroup
                                value={section.value}
                                onChange={(e) => section.onChange(e.target.value)}
                            >
                                {section.options.map((opt) => (
                                    <FormControlLabel
                                        key={opt.value}
                                        value={opt.value}
                                        control={<Radio color="success" />}
                                        className="py-1"
                                        label={<Typography className="text-gray-700">{opt.label}</Typography>}
                                    />
                                ))}
                            </RadioGroup>
                        </div>
                    ))}

                    {/* Actions */}
                    {!hidden_actions && (
                        <div className="mt-5 flex items-center justify-between">
                            <Button
                                variant="text"
                                color="inherit"
                                onClick={() => {
                                    onClear?.();
                                }}
                                className="normal-case"
                            >
                                ล้างตัวกรอง
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={() => {
                                    onApply?.();
                                    handleClose();
                                }}
                                className="normal-case rounded-xl"
                            >
                                ใช้ตัวกรอง
                            </Button>
                        </div>
                    )}
                </div>
            </Popover>
        </>
    );
};

export default PopoverFilter;
